import { getInitialValues } from "domains/user-management/models/ResidentialUserProfile/utils/getInitialValues";
import { ResidentialUserProfileValues } from "domains/user-management/models/ResidentialUserProfile/types/ResidentialUserProfileValues";
import { useFormik } from "formik";
import { useFetchSelector } from "helpers/hooks/useFetchSelector.hook";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  buildingActions,
  GET_BUILDING,
} from "slices/building/building.actions";
import { buildingsActions } from "slices/buildings/buildings.actions";
import {
  buildingsByMarketActions,
  GET_BUILDINGS_BY_MARKET,
} from "slices/buildingsByMarket/buildingsByMarket.actions";
import {
  currentUserActions,
  LOAD_MY_USER,
  SAVE_MY_USER,
} from "slices/currentUser/currentUser.actions";
import { IResidentialUserStoreType } from "slices/currentUser/currentUser.reducer";
import { GET_MARKETS, marketsActions } from "slices/markets/markets.actions";
import {
  PackageDeliveryPolicies,
  PackageDeliveryPreferencesEnum,
} from "types/generalSettingsEnums";
import {
  getBuildingsByMarketOptions,
  getBuildingUnitOptions,
  getMarketOptions,
} from "views/components/forms/helpers/select-options";
import { FetchDesignSelectOptionType } from "views/fetch-design/FetchDesignSelect/FetchDesignSelect";
import { MyResidentialUserProfileModel } from "./MyResidentialUserProfileModel";
import { getNowInApiDateTimeString } from "helpers/date-time.helper";

export const useMyResidentialUserProfileModel = function (
  config?: Partial<{
    handleSelectLeaveWithoutWaiver: () => void;
    onAcceptWaiver: () => void;
    onSaveUserSuccess: () => void;
  }>,
): MyResidentialUserProfileModel {
  const dispatch = useDispatch();
  const loading = useFetchSelector("loading");
  const residentialUser = useFetchSelector("residentialUser");
  const markets = useFetchSelector("markets");
  const buildingsByMarket = useFetchSelector("buildingsByMarket");
  const building = useFetchSelector("building");
  const marketOptions = useMemo(() => {
    return getMarketOptions(markets);
  }, [markets]);
  const buildingOptions = useMemo(() => {
    return getBuildingsByMarketOptions(buildingsByMarket);
  }, [buildingsByMarket]);
  const buildingUnitOptions = useMemo(() => {
    return getBuildingUnitOptions(building.units);
  }, [building.units]);
  const isLoading =
    loading[SAVE_MY_USER] ||
    loading[LOAD_MY_USER] ||
    loading[GET_MARKETS] ||
    loading[GET_BUILDINGS_BY_MARKET] ||
    loading[GET_BUILDING];
  const canToggleDeliveryPreference =
    building.packageDeliveryPolicy !==
    PackageDeliveryPolicies.BUILDING_DO_NOT_LEAVE;

  const handleFormSubmit = useCallback(
    ({
      buildingUnitId,
      packageDeliveryPreference,
      leaveLiabilityAccepted,
      termsAccepted,
    }: Partial<IResidentialUserStoreType>) => {
      const residentialUser: Partial<IResidentialUserStoreType> = {
        buildingUnitId,
        packageDeliveryPreference,
        termsAccepted: getNowInApiDateTimeString(),
      };
      if (termsAccepted) {
        residentialUser.termsAccepted = getNowInApiDateTimeString();
      }
      if (
        packageDeliveryPreference === PackageDeliveryPreferencesEnum.LEAVE &&
        leaveLiabilityAccepted
      ) {
        residentialUser.leaveLiabilityAccepted = getNowInApiDateTimeString();
      }
      dispatch(
        currentUserActions.saveMyResidentialUser(residentialUser, {
          onSuccess: () => {
            dispatch(currentUserActions.loadMyUser());
            if (config?.onSaveUserSuccess) {
              config.onSaveUserSuccess();
            }
          },
        }),
      );
    },
    [config, dispatch],
  );

  const residentialUserProfileForm = useFormik<ResidentialUserProfileValues>({
    enableReinitialize: true,
    initialErrors: {
      termsAccepted: "Required",
    },
    initialValues: {
      market: null,
      building: null,
      buildingUnit: null,
    },
    onSubmit: (values) => {
      if (
        residentialUser &&
        typeof values.market?.value === "number" &&
        typeof values.building?.value === "number" &&
        typeof values.buildingUnit?.value === "number"
      ) {
        handleFormSubmit({
          market: values?.market?.value,
          buildingId: values?.building?.value,
          buildingUnitId: values?.buildingUnit?.value,
          packageDeliveryPreference:
            typeof values.leaveAtDoor === "boolean"
              ? values.leaveAtDoor
                ? PackageDeliveryPreferencesEnum.LEAVE
                : PackageDeliveryPreferencesEnum.DO_NOT_LEAVE
              : PackageDeliveryPreferencesEnum.DO_NOT_LEAVE,
          termsAccepted: values.termsAccepted
            ? getNowInApiDateTimeString()
            : null,
          leaveLiabilityAccepted: values.termsAccepted
            ? getNowInApiDateTimeString()
            : undefined,
        });
      }
    },
    validate: (values) => {
      const errors: Partial<Record<keyof typeof values, string>> = {};

      if (typeof values.market?.value !== "number") {
        errors.market = "Required";
      }
      if (typeof values.building?.value !== "number") {
        errors.building = "Required";
      }
      if (typeof values.buildingUnit?.value !== "number") {
        errors.buildingUnit = "Required";
      }
      if (!values.termsAccepted) {
        errors.termsAccepted = "Required";
      }
      if (values.leaveAtDoor && !values.leaveLiabilityAccepted) {
        errors.leaveAtDoor = "Must accept leave liability when leave at door";
      }

      return errors;
    },
    isInitialValid: false,
  });

  const getMarkets = useCallback(() => {
    dispatch(marketsActions.getMarkets());
  }, [dispatch]);
  const getBuildingsByMarket = useCallback(
    (marketId: string) => {
      dispatch(buildingsByMarketActions.getBuildingsByMarket(marketId));
      dispatch(buildingActions.clearBuilding());
    },
    [dispatch],
  );
  const getBuilding = useCallback(
    (buildingId: string) => {
      dispatch(buildingActions.getBuilding(buildingId));
    },
    [dispatch],
  );
  const acceptWaiver = useCallback(() => {
    void residentialUserProfileForm.setValues({
      ...residentialUserProfileForm.values,
      leaveLiabilityAccepted: true,
      leaveAtDoor: true,
    });
    if (config?.onAcceptWaiver) {
      config?.onAcceptWaiver();
    }
  }, [config, residentialUserProfileForm]);
  const initializeForm = useCallback(
    (
      marketOptions: FetchDesignSelectOptionType[],
      residentialUser: IResidentialUserStoreType,
      buildingOptions: FetchDesignSelectOptionType[],
      buildingUnitOptions: FetchDesignSelectOptionType[],
      buildingLeavePolicy: PackageDeliveryPolicies,
    ): void => {
      void residentialUserProfileForm.setValues(
        getInitialValues(
          marketOptions,
          residentialUser,
          buildingOptions,
          buildingUnitOptions,
          buildingLeavePolicy,
        ),
      );
    },
    [residentialUserProfileForm],
  );
  const preloadForm: MyResidentialUserProfileModel["preloadForm"] = useCallback(
    (providedBuildingShortCode) => {
      dispatch(
        buildingsActions.searchBuildings(
          {
            filters: [
              {
                id: "building",
                value: providedBuildingShortCode,
              },
            ],
          },
          {
            onSuccess: (response) => {
              const building = response.content.find((building) => {
                return building.shortCode === providedBuildingShortCode;
              });

              if (building) {
                const buildingOption: FetchDesignSelectOptionType = {
                  label: building.name,
                  value: building.buildingId,
                };
                const marketOption: FetchDesignSelectOptionType | null =
                  marketOptions.find((option) => {
                    switch (typeof option.value) {
                      case "string":
                        return option.value === building.market.toString();
                      case "number":
                        return option.value === building.market;
                    }
                  }) || null;
                void residentialUserProfileForm.setValues({
                  ...residentialUserProfileForm.values,
                  market: marketOption,
                  building: buildingOption,
                });
              }
            },
          },
        ),
      );
    },
    [dispatch, marketOptions, residentialUserProfileForm],
  );

  // Load market options on component load
  useEffect(() => {
    getMarkets();
  }, []);
  // Load building options
  const marketId =
    residentialUserProfileForm.values.market?.value || residentialUser.market;
  useEffect(() => {
    if (!!marketId) {
      getBuildingsByMarket(marketId.toString());
    }
  }, [getBuildingsByMarket, marketId]);
  // Load building unit options
  const buildingId =
    residentialUserProfileForm.values.building?.value ||
    residentialUser.buildingId;
  useEffect(() => {
    if (buildingId) {
      getBuilding(buildingId.toString());
    }
  }, [buildingId, getBuilding]);
  // Set resident values if provided
  useEffect(() => {
    if (
      !!residentialUser &&
      !residentialUserProfileForm.values.market &&
      marketOptions.length &&
      buildingOptions.length &&
      buildingUnitOptions.length &&
      building.packageDeliveryPolicy
    ) {
      initializeForm(
        marketOptions,
        residentialUser,
        buildingOptions,
        buildingUnitOptions,
        building.packageDeliveryPolicy,
      );
    }
  }, [
    building.packageDeliveryPolicy,
    buildingOptions,
    buildingUnitOptions,
    initializeForm,
    marketOptions,
    residentialUser,
    residentialUserProfileForm.values.market,
  ]);

  return {
    acceptTerms(accepted) {
      if (accepted) {
        void residentialUserProfileForm.setFieldValue("termsAccepted", true);
      } else {
        void residentialUserProfileForm.setFieldValue(
          "termsAccepted",
          undefined,
        );
      }
    },
    acceptWaiver,
    buildingOptions,
    buildingUnitOptions,
    canToggleDeliveryPreference,
    deliveryPreferenceSetByBuilding:
      building.packageDeliveryPolicy ===
      PackageDeliveryPolicies.BUILDING_DO_NOT_LEAVE,
    errors: residentialUserProfileForm.errors,
    handleSubmit: residentialUserProfileForm.handleSubmit,
    isLoading,
    isValid: residentialUserProfileForm.isValid,
    marketOptions,
    preloadForm,
    registrationHelp:
      typeof building?.meta === "object" &&
      "registrationHelp" in building.meta &&
      typeof building.meta.registrationHelp === "string"
        ? {
            buildingName: building.name,
            instructions: building.meta.registrationHelp,
          }
        : undefined,
    residentPrivacyPolicyUrl: building.residentPrivacyPolicyUrl,
    residentTermsAndConditionsUrl: building.residentTermsAndConditionsUrl,
    selectBuilding: (option) => {
      void residentialUserProfileForm.setValues({
        ...residentialUserProfileForm.values,
        building: option || null,
        buildingUnit: null,
      });
    },
    selectBuildingUnit(buildingUnit) {
      void residentialUserProfileForm.setFieldValue(
        "buildingUnit",
        buildingUnit,
      );
    },
    selectDeliveryPreference(deliveryPreference) {
      if (deliveryPreference) {
        if (!residentialUserProfileForm.values.leaveLiabilityAccepted) {
          if (config?.handleSelectLeaveWithoutWaiver) {
            config?.handleSelectLeaveWithoutWaiver();
          } else {
            throw new Error("Unhandled leave liability state");
          }
        } else {
          void residentialUserProfileForm.setFieldValue("leaveAtDoor", true);
        }
      } else {
        void residentialUserProfileForm.setFieldValue("leaveAtDoor", false);
      }
    },
    selectMarket: (option) => {
      void residentialUserProfileForm.setValues({
        ...residentialUserProfileForm.values,
        market: option || null,
        building: null,
        buildingUnit: null,
      });
    },
    values: residentialUserProfileForm.values,
  };
};
